import React from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Grid, Typography, Link, IconButton, Stack, Divider, useTheme, alpha } from '@mui/material';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import FacebookIcon from '@mui/icons-material/Facebook';
import InstagramIcon from '@mui/icons-material/Instagram';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import Logo from '../../assets/saraya_logo_new.png';
import LogoDark from '../../assets/saraya_logo_dark.png';

const Footer = () => {
    const { t } = useTranslation();
    const theme = useTheme();

    return (
        <Box
            component="footer"
            sx={{
                bgcolor: theme.palette.mode === 'light' ? 'white' : 'background.paper',
                pt: 8,
                pb: 4,
                borderTop: `1px solid ${theme.palette.divider}`,
                position: 'relative',
                overflow: 'hidden',
                width: '100%',
                maxWidth: '100%',
                mx: 'auto',
                px: { xs: 3, sm: 4, md: 6, lg: 8, xl: 10 },
            }}
        >
            <Box sx={{
                maxWidth: '100%',
                mx: 'auto',
                px: { xs: 0, sm: 2, md: 3 }
            }}>
                <Grid container spacing={6} justifyContent="center">
                    {/* Brand Column */}
                    <Grid item xs={12} md={4} xl={3}>
                        <Box display="flex" alignItems="center" gap={1} mb={2}>
                            <Box
                                component="img"
                                src={theme.palette.mode === 'dark' ? LogoDark : Logo}
                                alt="Saraya Auto Sales"
                                sx={{
                                    height: 70,
                                    width: 'auto',
                                    objectFit: 'contain'
                                }}
                            />

                        </Box>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 3, maxWidth: 300, lineHeight: 1.8 }}>
                            {t('footer.description')}
                        </Typography>
                        <Stack direction="row" spacing={1}>

                            <IconButton
                                component="a"
                                href="https://www.facebook.com/sarayaautosales"
                                target="_blank"
                                rel="noopener noreferrer"
                                aria-label="Facebook"
                                size="small"
                                color="primary"
                                sx={{ bgcolor: alpha(theme.palette.primary.main, 0.1), '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.2) } }}
                            >
                                <FacebookIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                                component="a"
                                href="https://www.instagram.com/sarayaautosales/"
                                target="_blank"
                                rel="noopener noreferrer"
                                aria-label="Instagram"
                                size="small"
                                color="primary"
                                sx={{ bgcolor: alpha(theme.palette.primary.main, 0.1), '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.2) } }}
                            >
                                <InstagramIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                                component="a"
                                href="https://www.linkedin.com/company/saraya-auto-sales"
                                target="_blank"
                                rel="noopener noreferrer"
                                aria-label="LinkedIn"
                                size="small"
                                color="primary"
                                sx={{ bgcolor: alpha(theme.palette.primary.main, 0.1), '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.2) } }}
                            >
                                <LinkedInIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                                component="a"
                                href="https://www.tiktok.com/@sarayaautosales"
                                target="_blank"
                                rel="noopener noreferrer"
                                aria-label="TikTok"
                                size="small"
                                color="primary"
                                sx={{ bgcolor: alpha(theme.palette.primary.main, 0.1), '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.2) } }}
                            >
                                {/* Custom TikTok Icon SVG path */}
                                <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
                                    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z" />
                                </svg>
                            </IconButton>
                        </Stack>
                    </Grid>

                    {/* Quick Links */}
                    <Grid item xs={6} sm={4} md={2} xl={2}>
                        <Typography variant="subtitle1" component="h3" fontWeight={700} gutterBottom sx={{
                            mb: 2,
                            color: theme.palette.mode === 'light' ? 'secondary.main' : 'text.primary'
                        }}>
                            {t('footer.navigation')}
                        </Typography>
                        <Stack spacing={1.5}>
                            {[t('nav.home'), t('footer.vehicles'), 'Services', t('nav.about'), t('nav.contact')].map((item) => (
                                <Link
                                    key={item}
                                    href="#"
                                    color="text.secondary"
                                    underline="hover"
                                    sx={{
                                        fontSize: '0.95rem',
                                        transition: 'color 0.2s',
                                        '&:hover': { color: 'primary.main' }
                                    }}
                                >
                                    {item}
                                </Link>
                            ))}
                        </Stack>
                    </Grid>

                    {/* Services */}
                    <Grid item xs={6} sm={4} md={2} xl={2}>
                        <Typography variant="subtitle1" component="h3" fontWeight={700} gutterBottom sx={{
                            mb: 2,
                            color: theme.palette.mode === 'light' ? 'secondary.main' : 'text.primary'
                        }}>
                            {t('footer.services')}
                        </Typography>
                        <Stack spacing={1.5}>
                            {[t('footer.buy'), t('footer.sell'), t('footer.tradeIn'), t('footer.financing'), t('footer.warranty')].map((item) => (
                                <Link
                                    key={item}
                                    href="#"
                                    color="text.secondary"
                                    underline="hover"
                                    sx={{
                                        fontSize: '0.95rem',
                                        transition: 'color 0.2s',
                                        '&:hover': { color: 'primary.main' }
                                    }}
                                >
                                    {item}
                                </Link>
                            ))}
                        </Stack>
                    </Grid>

                    {/* Contact Info */}
                    <Grid item xs={12} sm={4} md={4} xl={3}>
                        <Typography variant="subtitle1" component="h3" fontWeight={700} gutterBottom sx={{
                            mb: 2,
                            color: theme.palette.mode === 'light' ? 'secondary.main' : 'text.primary'
                        }}>
                            {t('footer.contact')}
                        </Typography>
                        <Stack spacing={2}>
                            <Box display="flex" gap={2}>
                                <LocationOnIcon color="primary" fontSize="small" sx={{ mt: 0.5 }} />
                                <Typography variant="body2" color="text.secondary">
                                    Sicap Liberté 4 Villa N°5181,
                                    Dakar Sénégal
                                </Typography>
                            </Box>
                            <Box display="flex" gap={2}>
                                <PhoneIcon color="primary" fontSize="small" sx={{ mt: 0.5 }} />
                                <Typography variant="body2" color="text.secondary">
                                    +221 77 415 65 65
                                </Typography>
                            </Box>
                            <Box display="flex" gap={2}>
                                <EmailIcon color="primary" fontSize="small" sx={{ mt: 0.5 }} />
                                <Typography variant="body2" color="text.secondary">
                                    info@saraya.tech
                                </Typography>
                            </Box>
                        </Stack>
                    </Grid>
                </Grid>

                <Divider sx={{ my: 4, mx: { xs: 0, sm: 2, md: 3 } }} />

                <Box
                    display="flex"
                    flexDirection={{ xs: 'column', sm: 'row' }}
                    justifyContent="space-between"
                    alignItems="center"
                    gap={2}
                    sx={{ px: { xs: 0, sm: 2, md: 3 } }}
                >
                    <Typography variant="body2" color="text.secondary">
                        {t('footer.copyright', { year: new Date().getFullYear() })}
                    </Typography>
                    <Stack direction="row" spacing={3}>
                        <Link href="#" color="text.secondary" underline="hover" variant="body2">
                            {t('footer.legal')}
                        </Link>
                        <Link href="#" color="text.secondary" underline="hover" variant="body2">
                            {t('footer.privacy')}
                        </Link>
                        <Link href="#" color="text.secondary" underline="hover" variant="body2">
                            {t('footer.terms')}
                        </Link>
                    </Stack>
                </Box>
            </Box>
        </Box>
    );
};

export default Footer;
